import './styles/fonts.css';
import './styles/theme.css';
import './styles/index.css';
import './styles/app.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { escapeHtml } from '@/app/lib/html-escape';
import App from './app/App';
import { trackTelemetryError, trackTelemetryEvent } from './app/lib/telemetry';

// Initialize Notification polyfill (before anything else that might use Notification API)
import './app/utils/notification-polyfill';

// Initialize mobile debugging (dev only — not for consumer-facing production)
if (import.meta.env.DEV) {
  import('./app/utils/mobileDebug').then(({ initMobileDebug }) => initMobileDebug());
}

const CHUNK_RECOVERY_FLAG = 'tension_chunk_recovery_once';

function isLikelyChunkLoadError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('failed to fetch dynamically imported module') ||
    m.includes('loading chunk') ||
    m.includes('chunkloaderror') ||
    m.includes('importing a module script failed') ||
    m.includes('unexpected token \'<\'')
  );
}

function tryChunkRecovery(reason: unknown): void {
  let fallback = '[unserializable-reason]';
  try {
    fallback = JSON.stringify(reason);
  } catch {
    fallback = String(reason);
  }
  const message = reason instanceof Error
    ? `${reason.name}: ${reason.message}`
    : typeof reason === 'string'
      ? reason
      : fallback;

  if (!isLikelyChunkLoadError(message)) return;

  try {
    if (sessionStorage.getItem(CHUNK_RECOVERY_FLAG) === '1') return;
    sessionStorage.setItem(CHUNK_RECOVERY_FLAG, '1');
  } catch {
    // ignore storage failures; still attempt a hard reload
  }

  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now().toString());
  void trackTelemetryEvent('chunk_recovery_triggered', {
    path: window.location.pathname,
    reason: reason instanceof Error ? reason.message : String(reason),
  });
  window.location.replace(url.toString());
}

async function clearLegacyWebCaches(): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // Non-blocking cache cleanup.
  }
}

window.addEventListener('error', (event) => {
  tryChunkRecovery(event.error ?? event.message);
});

window.addEventListener('unhandledrejection', (event) => {
  tryChunkRecovery(event.reason);
});

const rootElement = document.getElementById('root');

if (typeof document !== 'undefined') {
  document.documentElement.dataset.designSystem = 'unified';
  document.documentElement.classList.add('dark');
  document.body.classList.add('dark');
  document.documentElement.style.colorScheme = 'dark';
}

if (!rootElement) {
  throw new Error('Root element not found');
}

try {
  void clearLegacyWebCaches();
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
  try {
    sessionStorage.removeItem(CHUNK_RECOVERY_FLAG);
  } catch {
    // ignore
  }
} catch (error) {
  console.error('🚨 FATAL ERROR during render:', error);
  void trackTelemetryError(error, {
    stage: 'app_bootstrap',
    path: window.location.pathname,
  });

  const showDetails = import.meta.env.DEV;
  const details = showDetails
    ? escapeHtml(error instanceof Error ? error.stack ?? error.message : String(error))
    : 'A startup failure occurred. Please reload. If this continues, contact support.';

  // Show emergency error UI (escape user-controlled error text to avoid HTML injection)
  rootElement.innerHTML = `
    <div style="padding: 40px; background: #000; min-height: 100vh; color: white; font-family: system-ui;">
      <h1 style="color: #ef4444; margin-bottom: 20px;">⚠️ App Failed to Load</h1>
      <p style="margin-bottom: 20px;">The TENSION app encountered a fatal error during initialization.</p>
      <pre style="background: #1a1a1a; padding: 15px; border-radius: 8px; overflow-x: auto; font-size: 12px;">
${details}
      </pre>
      <button type="button" id="tension-fatal-reload" style="margin-top: 30px; background: #8b5cf6; color: white; padding: 12px 24px; border: none; border-radius: 6px; font-weight: 600; cursor: pointer;">Reload Page</button>
    </div>
  `;
  document.getElementById('tension-fatal-reload')?.addEventListener('click', () => {
    location.reload();
  });
}
